const allowedEvents = [
    'page_view', // implemented
    'registration_start', // implemented
    'registration_step_complete', // implemented
    'registration_abandon', // implemented
    'registration_complete', // implemented
    'login', // implemented
    'logout', // implemented
    'vehicle_list_view', // implemented
    'vehicle_search', // implemented
    'filter_applied', // implemented
    'vehicle_detail_view', // implemented
    'image_gallery_view', // implemented
    'inspection_report_view', // implemented
    'auction_view', // implemented
    'auction_follow', // implemented
    'auction_watchlist_add', // implemented
    'auction_watchlist_remove', // implemented
    'auction_enter', // implemented
    'auction_bid_start', // implemented
    'auction_bid_submit', // implemented
    'auction_bid_outbid', // not implemented
    'auction_bid_win', // not implemented
    'auction_bid_lost', // not implemented
    'auction_closed', // implemented
    'transaction_created', // not implemented
    'payment_initiated', // implemented
    'payment_success', // implemented
    'payment_failed', // not implemented
    'auction_purchase', // not implemented
    'commission_generated', // not implemented
    'lead_created', // not implemented
    'lead_qualified', // not implemented
    'contact_request', // implemented
    'whatsapp_click', // implemented
    'call_click', // implemented
    'email_click', // implemented
    'form_start', // implemented
    'form_step_complete', // implemented
    'form_submit', // implemented
    'form_abandon', // implemented
    'form_error', // implemented
    'field_error', // implemented
    'field_completed', // implemented
    'scroll_depth', // implemented
    'click_cta', // implemented
    'banner_click', // implemented
    'faq_expand', // implemented
    'session_engaged', // not implemented
    'push_sent', // not implemented
    'push_opened', // not implemented
    'push_clicked', // not implemented
    'email_sent', // not implemented
    'email_opened', // not implemented
    'email_clicked', // not implemented
    'user_returned', // not implemented
    'reactivated_user', // not implemented
    'lead_sent_to_crm', // not implemented
    'lead_assigned', // not implemented
    'lead_contacted', // not implemented
    'lead_closed', // not implemented
    'lead_won', // not implemented
    'lead_lost', // not implemented
    'utm_redirect', // implemented
    'ad_visit', // implemented — landing from Google Ads / Snapchat / TikTok
]

const allowedEventSet = new Set(allowedEvents)
const formStateById = new Map()
const scrollMilestones = new Set()

const AD_ATTRIBUTION_STORAGE_KEY = 'analytics_ad_attribution'
const AD_VISIT_TRACKED_KEY = 'analytics_ad_visit_tracked'

let isWebTrackingInitialized = false
let hasTrackedAdVisitThisLoad = false
let isGoogleAdsInitialized = false

function getGoogleAdsConversionId() {
    return normalizeText(process.env.VUE_APP_GOOGLE_ADS_ID)
}

function getGoogleAdsSignupSendTo() {
    const sendTo = normalizeText(process.env.VUE_APP_GOOGLE_ADS_SIGNUP_SEND_TO)
    if (sendTo) {
        return sendTo
    }

    const conversionId = getGoogleAdsConversionId()
    const conversionLabel = normalizeText(process.env.VUE_APP_GOOGLE_ADS_SIGNUP_LABEL)
    if (conversionId && conversionLabel) {
        return `${conversionId}/${conversionLabel}`
    }

    return undefined
}

function getGoogleAdsSignupValue() {
    const rawValue = process.env.VUE_APP_GOOGLE_ADS_SIGNUP_VALUE
    if (rawValue === undefined || rawValue === null || rawValue === '') {
        return undefined
    }

    const parsed = Number(rawValue)
    return Number.isFinite(parsed) ? parsed : undefined
}

function getGoogleAdsCurrency() {
    return normalizeText(process.env.VUE_APP_GOOGLE_ADS_CURRENCY) || 'SAR'
}

function splitFullName(name) {
    const normalized = normalizeText(name)
    if (!normalized) return {}

    const parts = normalized.split(' ')
    if (parts.length === 1) {
        return { first_name: parts[0] }
    }

    return {
        first_name: parts[0],
        last_name: parts.slice(1).join(' '),
    }
}

function formatE164Phone(phoneNumber, countryData) {
    if (!phoneNumber) return undefined

    const digits = String(phoneNumber).replace(/\D/g, '')
    if (!digits) return undefined

    if (String(phoneNumber).trim().startsWith('+')) {
        return `+${digits}`
    }

    const dialCode = countryData?.dialCode
        ? String(countryData.dialCode).replace(/\D/g, '')
        : ''

    if (dialCode) {
        return `+${dialCode}${digits}`
    }

    return undefined
}

function buildUserProvidedData(payload = {}) {
    const nameParts = splitFullName(payload.name || payload.full_name)
    const phoneNumber = formatE164Phone(
        payload.phone_number || payload.phone,
        payload.country_data
    )
    const countryCode = payload.country_data?.iso2 || payload.country

    const address = removeEmptyValues({
        ...nameParts,
        country: countryCode ? String(countryCode).toUpperCase() : undefined,
        postal_code: payload.postal_code || undefined,
    })

    return removeEmptyValues({
        email: payload.email || undefined,
        phone_number: phoneNumber,
        ...(Object.keys(address).length > 0 ? { address } : {}),
    })
}

function pushGtmLegacyEvent(eventPayload, legacyEventName) {
    if (!legacyEventName || typeof window === 'undefined') {
        return
    }

    const userData = buildUserProvidedData(eventPayload)
    const address = userData.address || {}

    window.dataLayer = window.dataLayer || []
    window.dataLayer.push(removeEmptyValues({
        ...eventPayload,
        event: legacyEventName,
        ...(Object.keys(userData).length > 0 ? { user_data: userData } : {}),
        phone_number: userData.phone_number,
        email: userData.email,
        first_name: address.first_name,
        last_name: address.last_name,
        name: eventPayload.name || eventPayload.full_name,
    }))
}

export function initGoogleAds() {
    if (isGoogleAdsInitialized || typeof window === 'undefined') {
        return
    }

    const conversionId = getGoogleAdsConversionId()
    if (!conversionId || typeof window.gtag !== 'function') {
        return
    }

    isGoogleAdsInitialized = true
    window.gtag('config', conversionId)
}

export function trackGoogleAdsSignupConversion(payload = {}, options = {}) {
    initGoogleAds()

    const sendTo = getGoogleAdsSignupSendTo()
    if (!sendTo || typeof window === 'undefined') {
        return false
    }

    const value = getGoogleAdsSignupValue()
    const conversionPayload = removeEmptyValues({
        send_to: sendTo,
        ...(value !== undefined
            ? {
                value,
                currency: getGoogleAdsCurrency(),
            }
            : {}),
        transaction_id: payload.event_id || payload.user_id || undefined,
    })

    window.dataLayer = window.dataLayer || []
    window.dataLayer.push({
        event: 'google_ads_signup_conversion',
        google_ads_send_to: sendTo,
        ...conversionPayload,
        ...payload,
    })

    const eventCallback = typeof options.event_callback === 'function'
        ? options.event_callback
        : undefined
    const eventTimeout = options.event_timeout || 2000

    if (typeof window.gtag === 'function') {
        window.gtag('event', 'conversion', removeEmptyValues({
            ...conversionPayload,
            ...(eventCallback
                ? {
                    event_callback: eventCallback,
                    event_timeout: eventTimeout,
                }
                : {}),
        }))
    } else if (eventCallback) {
        eventCallback()
    }

    return true
}

function getAppEnvironment() {
    return process.env.VUE_APP_ANALYTICS_ENV ||
        process.env.VUE_APP_ENV ||
        process.env.NODE_ENV ||
        'development'
}

function getDeviceType() {
    if (typeof window === 'undefined') return 'server'

    return window.matchMedia && window.matchMedia('(max-width: 767px)').matches
        ? 'mobile'
        : 'desktop'
}

function getStoredUserId() {
    if (typeof window === 'undefined') return undefined

    return window.localStorage.getItem('auth') || undefined
}

function createEventId(eventName) {
    return `${eventName}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
}

function removeEmptyValues(payload) {
    return Object.keys(payload).reduce((result, key) => {
        if (payload[key] !== undefined && payload[key] !== null) {
            result[key] = payload[key]
        }

        return result
    }, {})
}

function getPathWithoutOrigin(urlString) {
    if (!urlString) return undefined

    try {
        const parsed = new URL(urlString, window.location.origin)
        return `${parsed.pathname}${parsed.search}`
    } catch (error) {
        return undefined
    }
}

function normalizeText(value) {
    if (!value || typeof value !== 'string') return undefined

    const normalized = value.replace(/\s+/g, ' ').trim()
    return normalized || undefined
}

function getQueryParam(query, key) {
    if (!query || typeof query !== 'object') return undefined

    const value = query[key]
    if (Array.isArray(value)) {
        return normalizeText(String(value[0] || ''))
    }

    if (value === undefined || value === null) return undefined
    return normalizeText(String(value))
}

function normalizeUtmSource(utmSource) {
    if (!utmSource) return undefined

    const source = utmSource.toLowerCase()

    if (
        source.includes('google') ||
        source === 'gads' ||
        source === 'adwords' ||
        source === 'googleads'
    ) {
        return 'google_ads'
    }

    if (
        source.includes('snap') ||
        source === 'sc' ||
        source === 'snapchat'
    ) {
        return 'snapchat'
    }

    if (
        source.includes('tiktok') ||
        source === 'tt' ||
        source === 'bytedance'
    ) {
        return 'tiktok'
    }

    return undefined
}

/**
 * Detect paid ad traffic from click IDs and UTM params.
 * Google Ads example:
 *   ?gad_source=1&gad_campaignid=...&gclid=...&gbraid=...
 * Snapchat: ?ScCid=... or ?utm_source=snapchat
 * TikTok: ?ttclid=... or ?utm_source=tiktok
 */
export function detectAdTraffic(query = {}) {
    const gclid = getQueryParam(query, 'gclid')
    const gbraid = getQueryParam(query, 'gbraid')
    const wbraid = getQueryParam(query, 'wbraid')
    const gadSource = getQueryParam(query, 'gad_source')
    const gadCampaignId = getQueryParam(query, 'gad_campaignid')

    const scCid = getQueryParam(query, 'ScCid') || getQueryParam(query, 'sccid')
    const scClickId = getQueryParam(query, 'sc_click_id')

    const ttclid = getQueryParam(query, 'ttclid')

    const utmSource = getQueryParam(query, 'utm_source')
    const utmMedium = getQueryParam(query, 'utm_medium')
    const utmCampaign = getQueryParam(query, 'utm_campaign')
    const utmContent = getQueryParam(query, 'utm_content')
    const utmTerm = getQueryParam(query, 'utm_term')

    let trafficSource
    let clickId

    if (gclid || gbraid || wbraid || gadSource || gadCampaignId) {
        trafficSource = 'google_ads'
        clickId = gclid || gbraid || wbraid
    } else if (scCid || scClickId) {
        trafficSource = 'snapchat'
        clickId = scCid || scClickId
    } else if (ttclid) {
        trafficSource = 'tiktok'
        clickId = ttclid
    } else {
        trafficSource = normalizeUtmSource(utmSource)
    }

    if (!trafficSource) {
        return null
    }

    return removeEmptyValues({
        traffic_source: trafficSource,
        click_id: clickId,
        gclid,
        gbraid,
        wbraid,
        gad_source: gadSource,
        gad_campaignid: gadCampaignId,
        sccid: scCid || scClickId,
        ttclid,
        utm_source: utmSource,
        utm_medium: utmMedium,
        utm_campaign: utmCampaign,
        utm_content: utmContent,
        utm_term: utmTerm,
    })
}

function readStoredAdAttribution() {
    if (typeof window === 'undefined') return null

    try {
        const raw = window.sessionStorage.getItem(AD_ATTRIBUTION_STORAGE_KEY)
        return raw ? JSON.parse(raw) : null
    } catch (error) {
        return null
    }
}

function storeAdAttribution(attribution) {
    if (typeof window === 'undefined' || !attribution) return

    try {
        window.sessionStorage.setItem(
            AD_ATTRIBUTION_STORAGE_KEY,
            JSON.stringify({
                ...attribution,
                captured_at: Date.now(),
            })
        )
    } catch (error) {
        // Ignore storage failures (private mode / quota)
    }
}

function getAdVisitDedupeKey(attribution) {
    return [
        attribution.traffic_source,
        attribution.click_id,
        attribution.gad_campaignid,
        attribution.utm_campaign,
    ].filter(Boolean).join('|')
}

function hasAlreadyTrackedAdVisit(dedupeKey) {
    if (typeof window === 'undefined') return true

    try {
        return window.sessionStorage.getItem(AD_VISIT_TRACKED_KEY) === dedupeKey
    } catch (error) {
        return hasTrackedAdVisitThisLoad
    }
}

function markAdVisitTracked(dedupeKey) {
    hasTrackedAdVisitThisLoad = true

    if (typeof window === 'undefined') return

    try {
        window.sessionStorage.setItem(AD_VISIT_TRACKED_KEY, dedupeKey)
    } catch (error) {
        // Ignore storage failures
    }
}

/**
 * Fire ad_visit once when a visitor lands from Google Ads, Snapchat, or TikTok.
 * Call from the router with the route query object.
 */
export function trackAdVisitIfNeeded(query = {}, pagePath) {
    const attribution = detectAdTraffic(query)
    if (!attribution) {
        return false
    }

    storeAdAttribution(attribution)

    const dedupeKey = getAdVisitDedupeKey(attribution)
    if (hasAlreadyTrackedAdVisit(dedupeKey) || hasTrackedAdVisitThisLoad) {
        return false
    }

    markAdVisitTracked(dedupeKey)

    return track('ad_visit', removeEmptyValues({
        ...attribution,
        page_path: pagePath || (typeof window !== 'undefined' ? window.location.pathname : undefined),
    }))
}

export function getAdAttribution() {
    return readStoredAdAttribution()
}

function getElementText(element) {
    if (!element) return undefined

    return normalizeText(
        element.getAttribute('data-analytics-label') ||
        element.getAttribute('aria-label') ||
        element.innerText ||
        element.textContent
    )
}

function getFormId(formElement) {
    return formElement.id || formElement.getAttribute('name') || formElement.getAttribute('data-form-name') || 'unnamed_form'
}

function getOrCreateFormState(formElement) {
    const formId = getFormId(formElement)
    if (!formStateById.has(formId)) {
        formStateById.set(formId, {
            started: false,
            submitted: false,
            completedFields: new Set(),
            stepCompleteTracked: false,
            hasErrors: false,
        })
    }

    return { formId, state: formStateById.get(formId) }
}

function getRequiredInputs(formElement) {
    return Array.from(formElement.querySelectorAll('input[required], textarea[required], select[required]'))
}

function isFieldEmpty(inputElement) {
    if (!inputElement) return true

    if (inputElement.type === 'checkbox' || inputElement.type === 'radio') {
        return !inputElement.checked
    }

    return !normalizeText(String(inputElement.value || ''))
}

function trackFormStartIfNeeded(formElement) {
    const { formId, state } = getOrCreateFormState(formElement)
    if (state.started) return

    state.started = true
    track('form_start', { form_name: formId })
}

function trackFormStepCompletionIfNeeded(formElement, formId, state) {
    if (state.stepCompleteTracked) return

    const requiredInputs = getRequiredInputs(formElement)
    if (requiredInputs.length === 0) return

    const completedRequiredCount = requiredInputs.filter((input) => !isFieldEmpty(input)).length
    if (completedRequiredCount < Math.ceil(requiredInputs.length / 2)) return

    state.stepCompleteTracked = true
    track('form_step_complete', {
        form_name: formId,
        step_number: 1,
    })
}

export function initWebEngagementTracking(router) {
    if (isWebTrackingInitialized || typeof window === 'undefined' || typeof document === 'undefined') {
        return
    }

    isWebTrackingInitialized = true

    if (router && typeof router.afterEach === 'function') {
        router.afterEach(() => {
            scrollMilestones.clear()
            formStateById.clear()
        })
    }

    document.addEventListener('click', (event) => {
        const anchor = event.target.closest('a')
        if (anchor) {
            const href = anchor.getAttribute('href') || ''
            const payload = {
                click_text: getElementText(anchor),
                page_path: window.location.pathname,
                target_path: getPathWithoutOrigin(href),
            }

            if (href.startsWith('tel:')) {
                track('call_click', payload)
                track('contact_request', { ...payload, contact_channel: 'call' })
                return
            }

            if (href.startsWith('mailto:')) {
                track('email_click', payload)
                track('contact_request', { ...payload, contact_channel: 'email' })
                return
            }

            if (href.includes('api.whatsapp.com') || href.includes('wa.me')) {
                track('whatsapp_click', payload)
                track('contact_request', { ...payload, contact_channel: 'whatsapp' })
                return
            }
        }

        const ctaElement = event.target.closest('[data-analytics-cta], .btn.btn-primary, .btn.btn-success')
        if (ctaElement) {
            track('click_cta', {
                click_text: getElementText(ctaElement),
                cta_id: ctaElement.id || ctaElement.getAttribute('data-analytics-cta') || undefined,
                page_path: window.location.pathname,
            })
        }

        const bannerElement = event.target.closest('.banner, [class*="banner"]')
        if (bannerElement) {
            track('banner_click', {
                banner_id: bannerElement.id || bannerElement.getAttribute('data-banner-id') || undefined,
                click_text: getElementText(event.target.closest('a, button') || bannerElement),
                page_path: window.location.pathname,
            })
        }

        const faqTrigger = event.target.closest('.accordion [data-toggle="collapse"], [data-toggle="collapse"][data-parent*="accordion"]')
        if (faqTrigger) {
            track('faq_expand', {
                question: getElementText(faqTrigger),
                target_id: faqTrigger.getAttribute('data-target') || faqTrigger.getAttribute('aria-controls') || undefined,
                page_path: window.location.pathname,
            })
        }
    }, true)

    document.addEventListener('focusin', (event) => {
        const formElement = event.target.closest('form')
        if (!formElement) return

        trackFormStartIfNeeded(formElement)
    }, true)

    document.addEventListener('change', (event) => {
        const formElement = event.target.closest('form')
        if (!formElement) return

        const { formId, state } = getOrCreateFormState(formElement)
        trackFormStartIfNeeded(formElement)

        const fieldName = event.target.name || event.target.id
        if (fieldName && !isFieldEmpty(event.target) && !state.completedFields.has(fieldName)) {
            state.completedFields.add(fieldName)
            track('field_completed', {
                form_name: formId,
                field_name: fieldName,
            })
        }

        trackFormStepCompletionIfNeeded(formElement, formId, state)
    }, true)

    document.addEventListener('blur', (event) => {
        const formElement = event.target.closest('form')
        if (!formElement) return

        const inputElement = event.target
        if (!inputElement.name && !inputElement.id) return

        const isRequiredError = inputElement.required && isFieldEmpty(inputElement)
        const isEmailError = inputElement.type === 'email' && inputElement.value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(inputElement.value)

        if (isRequiredError || isEmailError) {
            const { formId, state } = getOrCreateFormState(formElement)
            state.hasErrors = true
            track('field_error', {
                form_name: formId,
                field_name: inputElement.name || inputElement.id,
                error_type: isRequiredError ? 'required' : 'invalid_email',
            })
        }
    }, true)

    document.addEventListener('submit', (event) => {
        const formElement = event.target
        if (!formElement || formElement.tagName !== 'FORM') return

        const { formId, state } = getOrCreateFormState(formElement)
        state.submitted = true

        const requiredInputs = getRequiredInputs(formElement)
        const hasRequiredErrors = requiredInputs.some((input) => isFieldEmpty(input))
        if (hasRequiredErrors || state.hasErrors) {
            track('form_error', {
                form_name: formId,
                error_type: hasRequiredErrors ? 'validation_required' : 'validation_error',
            })
        }

        track('form_submit', { form_name: formId })
    }, true)

    window.addEventListener('beforeunload', () => {
        formStateById.forEach((state, formId) => {
            if (state.started && !state.submitted) {
                track('form_abandon', { form_name: formId })
            }
        })
    })

    window.addEventListener('scroll', () => {
        const scrollableHeight = document.documentElement.scrollHeight - window.innerHeight
        if (scrollableHeight <= 0) return

        const depth = Math.round((window.scrollY / scrollableHeight) * 100)
        const milestones = [25, 50, 75, 90]

        milestones.forEach((milestone) => {
            if (depth >= milestone && !scrollMilestones.has(milestone)) {
                scrollMilestones.add(milestone)
                track('scroll_depth', {
                    depth_percent: milestone,
                    page_path: window.location.pathname,
                })
            }
        })
    }, { passive: true })
}

export function track(eventName, payload = {}, options = {}) {
    if (!eventName || typeof eventName !== 'string') {
        if (process.env.NODE_ENV !== 'production') {
            console.warn('[analytics] Ignored malformed analytics event name:', eventName)
        }

        return false
    }

    if (!allowedEventSet.has(eventName)) {
        if (process.env.NODE_ENV !== 'production') {
            console.warn(`[analytics] Ignored unapproved analytics event: ${eventName}`)
        }

        return false
    }

    if (typeof window === 'undefined') {
        return false
    }

    const userId = payload.user_id || getStoredUserId()
    const eventPayload = removeEmptyValues({
        ...payload,
        event: eventName,
        event_id: payload.event_id || createEventId(eventName),
        timestamp_ms: Date.now(),
        app_environment: "web : " + getAppEnvironment(),
        platform_type: "web",
        user_id: userId,
        device_type: getDeviceType(),
    })

    // Keep GTM / dataLayer consumers working
    window.dataLayer = window.dataLayer || []
    window.dataLayer.push(eventPayload)

    if (payload.legacy_event_name) {
        pushGtmLegacyEvent(eventPayload, payload.legacy_event_name)
    }

    const eventCallback = typeof options.event_callback === 'function'
        ? options.event_callback
        : undefined
    const eventTimeout = options.event_timeout || 2000

    // Send directly to GA4 so events do not depend on GTM tag mapping
    if (typeof window.gtag === 'function') {
        if (userId) {
            window.gtag('set', { user_id: String(userId) })
        }

        const {
            event: _event,
            event_id,
            timestamp_ms,
            user_id: _userId,
            ...gtagParams
        } = eventPayload

        window.gtag('event', eventName, removeEmptyValues({
            ...gtagParams,
            event_id,
            timestamp_ms,
            ...(eventCallback
                ? {
                    event_callback: eventCallback,
                    event_timeout: eventTimeout,
                }
                : {}),
        }))
    } else if (eventCallback) {
        eventCallback()
    }

    return true
}

export default {
    track,
    initWebEngagementTracking,
    initGoogleAds,
    detectAdTraffic,
    trackAdVisitIfNeeded,
    getAdAttribution,
    trackGoogleAdsSignupConversion,
}
